import React, { useEffect } from "react";
import "./HereWeAre101Page.css";

const heroImagePath = "/legacy/hereweare101/img";

type FeatureSection = {
  image: string;
  imageAlt: string;
  note?: string;
  title?: string;
  items: string[];
};

const featureSections: FeatureSection[] = [
  {
    image: "Community.png",
    imageAlt: "Community",
    items: [
      "We designed our platform to be user-friendly for people across all generations with our 'one button' sign-in with your Google account or Apple ID. You can also choose to sign-in with your mobile number or FB, it will just take a few seconds longer.",
    ],
  },
  {
    title: "MAP",
    image: "Map.png",
    imageAlt: "Map",
    items: [
      "The first screen you will encounter as you open the app is the Map. This allows users to see where our verified hotspots are globally.",
      "Click on the venue logo to be taken to the spot.",
    ],
  },
  {
    title: "EXPLORE",
    image: "Explore.png",
    imageAlt: "Explore",
    items: [
      "Explore is the page listing of all the verified hotspots on HereWeAre.",
      "They can be displayed as card or grid view.",
      "The venues will appear according to the distance from the user in real-time.",
    ],
  },
  {
    title: "SPOTCHAT",
    image: "SpotChat.png",
    imageAlt: "SpotChat",
    items: [
      "Refers to the chat room at a verified location.",
      "You must be at the location to be able to enter SpotChat.",
      "Before entering SpotChat, you will land at the venue's information page. You may call the venue from here or get a direct link to the map for directions as well as details on the current shows.",
      "Public / No barriers.",
      "Photos & up to 20 seconds videos.",
      "Reply function.",
      "Tag function (@username) with a notification when you are being mentioned in a conversation. The Tag function will only work with users who have participated in the SpotChat.",
    ],
  },
  {
    title: "EXCLUSIVE MESSAGE",
    image: "Exclusive.png",
    imageAlt: "Exclusive Message",
    items: [
      "A special message exclusively for those who visit the venue.",
      "Receive rare information or benefits.",
      "Enjoy unique perks as a true fan and an actual visitor of the spot and performance.",
      "Experience a level of value far beyond ordinary promotional messages.",
    ],
  },
  {
    title: "DM",
    image: "DM.png",
    imageAlt: "DM",
    items: [
      "DM is only available once you have added each other as Friends.",
      "The Friend-list is private between you and your friends.",
      "DM Group chat function is available between friends.",
      "Non-active chats on DM can be archived as well as remove by exiting the chat.",
    ],
  },
  {
    title: "HereWeAre HQ",
    image: "HQ.png",
    imageAlt: "HereWeAre HQ",
    items: [
      "HereWeAre HQ is the ONLY SpotChat that allows any users to access at anytime from anywhere. This is to facilitate users with questions regarding the app. Users can also find out about new venues being verified across the world here.",
      "Our HQ is based in Seoul and being monitored regularly.",
    ],
  },
  {
    title: "PROFILE",
    image: "Profile.png",
    imageAlt: "Profile",
    items: [
      "We encourage users to enter their full name, short descriptions of themselves as well as a link to their website, public IG or YouTube channel, particularly those working in the arts. This is to encourage creatives to use the app as a networking tool.",
    ],
  },
  {
    title: "MY SPOTS",
    note: "*available from November",
    image: "MySpot.png",
    imageAlt: "My Spot",
    items: [
      "Each profile will be allowed to save up to 4 venues in My Spots.",
      "This will allow them to be able to access SpotChat on a read-only basis to keep informed about what other people have been saying about the show or what the venue/artists have been posting.",
      "Users are only allowed to change up to 1 venue in My Spots every 30 days.",
    ],
  },
  {
    title: "AIRCHAT",
    image: "AirChat.png",
    imageAlt: "AirChat",
    items: [
      "Refers to the profile bubbles within SpotChat.",
      "Profile bubbles are displayed based on radius/distance from the user.",
      "Emojis can be sent directly to another profile in AirChat to say hi, get their attention or to let them know you have arrived at the venue by press and hold the bubble of the desired user.",
    ],
  },
];

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path d="M5.33333 13.6667L2 11L5.33333 16.3333L14 7L5.33333 13.6667Z" fill="#6B7EFF" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      className="hwa101-button-arrow"
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15 9.99886C15 10.2199 14.9122 10.4318 14.756 10.5881L8.0893 17.2559C7.76389 17.5813 7.23626 17.5814 6.91079 17.256C6.58533 16.9306 6.58529 16.4029 6.9107 16.0775L12.9882 9.99902L6.91079 3.92264C6.58533 3.59723 6.58529 3.06959 6.9107 2.74413C7.23611 2.41866 7.76374 2.41862 8.08921 2.74403L14.7559 9.40962C14.9122 9.56589 15 9.77784 15 9.99886Z"
        fill="white"
      />
    </svg>
  );
}

function DownloadButton({ className = "" }: { className?: string }) {
  return (
    <a
      className={`hwa101-download ${className}`}
      href="https://link.hereweare.io/install"
      target="_blank"
      rel="noreferrer"
    >
      <span>Let's Connect Now</span>
      <ArrowIcon />
    </a>
  );
}

export default function HereWeAre101Page() {
  useEffect(() => {
    document.title = "HereWeAre";
  }, []);

  return (
    <main className="hwa101-page">
      <section id="HereWeAre101" className="hwa101-hero">
        <div className="hwa101-hero-copy">
          <h1>HereWeAre 101</h1>
          <p>
            Welcome to HereWeAre,
            <br />
            the first global community platform
            <br />
            designed by creatives for creatives.
          </p>
        </div>
        <div className="hwa101-logo-container">
          <img
            src={`${heroImagePath}/logo_big.png`}
            alt="HereWeAre logo"
            className="hwa101-logo"
          />
        </div>
        <DownloadButton />
      </section>

      <section id="Commuity" className="hwa101-community">
        <h2>JOIN THE COMMUNITY</h2>
        {featureSections.map((section, index) => (
          <React.Fragment key={section.title || section.image}>
            {index === 1 && <h2>FEATURES</h2>}
            <article className="hwa101-feature">
              {section.title && (
                <div className="hwa101-feature-title">{section.title}</div>
              )}
              {section.note && <p className="hwa101-note">{section.note}</p>}
              <div className="hwa101-feature-items">
                {section.items.map((item) => (
                  <div className="hwa101-feature-item" key={item}>
                    <CheckIcon />
                    <p>{item}</p>
                  </div>
                ))}
              </div>
              <div className="hwa101-feature-image">
                <img
                  src={`${heroImagePath}/screen/${section.image}`}
                  alt={section.imageAlt}
                />
              </div>
            </article>
          </React.Fragment>
        ))}
      </section>

      <footer className="hwa101-footer">
        <a href="https://link.hereweare.io/install" target="_blank" rel="noreferrer">
          Download Now
          <ArrowIcon />
        </a>
      </footer>
    </main>
  );
}
